import { ArtefactConfidence } from '../types';

export type ConfidenceTone = 'positive' | 'moderate' | 'low';

export interface DashboardConfidenceDisplay {
  value: number;
  percent: number;
  score: string;
}

const LEGACY_CONFIDENCE_SCORE_MAP: Record<string, number> = {
  high: 0.9,
  moderate: 0.6,
  low: 0.3,
};

const DEFAULT_CONFIDENCE_SCORE = LEGACY_CONFIDENCE_SCORE_MAP.moderate;

function clampConfidenceScore(score: number) {
  return Math.min(Math.max(score, 0), 1);
}

function formatConfidenceScoreValue(normalizedScore: number) {
  return Number((normalizedScore * 100).toFixed(1)).toString();
}

export function getConfidenceScore(
  score?: number | null,
  level?: string | null,
  fallbackScore: number | null = null,
): number | null {
  if (typeof score === 'number' && Number.isFinite(score)) {
    return clampConfidenceScore(score);
  }

  const mappedScore = LEGACY_CONFIDENCE_SCORE_MAP[level || ''];
  if (typeof mappedScore === 'number') {
    return mappedScore;
  }

  if (typeof fallbackScore === 'number' && Number.isFinite(fallbackScore)) {
    return clampConfidenceScore(fallbackScore);
  }

  return null;
}

export function formatConfidenceScore(
  score?: number | null,
  level?: string | null,
  fallbackScore: number | null = null,
): string | null {
  const normalizedScore = getConfidenceScore(score, level, fallbackScore);

  return normalizedScore === null ? null : formatConfidenceScoreValue(normalizedScore);
}

export function getConfidenceTone(
  score?: number | null,
  level?: string | null,
  fallbackTone: ConfidenceTone = 'moderate',
): ConfidenceTone {
  const normalizedScore = getConfidenceScore(score, level);
  if (normalizedScore === null) {
    return fallbackTone;
  }

  if (normalizedScore >= 0.7) {
    return 'positive';
  }

  if (normalizedScore >= 0.5) {
    return 'moderate';
  }

  return 'low';
}

export function getConfidenceDisplay(
  confidence?: Pick<ArtefactConfidence, 'overall' | 'overallScore'> | null,
): DashboardConfidenceDisplay {
  const value =
    getConfidenceScore(confidence?.overallScore, confidence?.overall, DEFAULT_CONFIDENCE_SCORE) ??
    DEFAULT_CONFIDENCE_SCORE;
  const percent = Math.round(value * 100);

  return {
    value,
    percent,
    score: formatConfidenceScoreValue(value),
  };
}
